import React, { useEffect, useState } from 'react';

export interface RimInventoryItem {
  id: string;
  brand: string;
  model: string;
  size: string;
  diameter?: number | null;
  width?: number | null;
  rim_offset?: number | null;
  bolt_pattern?: string | null;
  pcd?: number | null;
  center_bore?: number | null;
  finish?: string | null;
  material?: string | null;
  price: number;
  quantity: number;
  image_url?: string | null;
  gallery?: string[] | null;
  features?: string[] | null;
  description?: string | null;
}

interface RimInventoryCardProps {
  rim: RimInventoryItem;
}

const RimInventoryCard: React.FC<RimInventoryCardProps> = ({ rim }) => {
  const [highlighted, setHighlighted] = useState(false);

  useEffect(() => {
    if (window.location.hash === `#rim-${rim.id}`) {
      setHighlighted(true);
      setTimeout(() => {
        document.getElementById(`rim-${rim.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      setTimeout(() => setHighlighted(false), 4000);
    }
  }, [rim.id]);

  const displaySize = rim.diameter && rim.width 
    ? `${rim.diameter}" x ${rim.width}"`
    : rim.size;

  const specs = [
    rim.bolt_pattern && `PCD: ${rim.bolt_pattern}`,
    rim.rim_offset && `Offset: ET${rim.rim_offset}`,
    rim.finish && `Finish: ${rim.finish}`
  ].filter(Boolean);

  if (!rim || rim.quantity <= 0) return null;

  return (
    <div
      id={`rim-${rim.id}`}
      style={{ scrollMarginTop: '80px' }}
      className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border group ${
        highlighted ? 'border-blue-500 ring-2 ring-blue-400 ring-offset-2' : 'border-gray-100'
      }`}
    >
      {/* Stock Badge */}
      <div className="relative">
        <div className="absolute top-3 right-3 z-10">
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            rim.quantity > 5 ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
          }`}>
            {rim.quantity > 5 ? 'In Stock' : 'Limited Stock'}
          </span>
        </div>
        
        {/* Rim Image */}
        <div className="h-48 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
          <div className="w-32 h-32 bg-gray-300 rounded-full overflow-hidden relative flex items-center justify-center">
            {rim.image_url ? (
              <img
                src={rim.image_url}
                alt={`${rim.brand} ${rim.model}`}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full relative flex items-center justify-center">
                <svg viewBox="0 0 200 200" className="w-full h-full">
                  <circle cx="100" cy="100" r="95" fill="#1F2937" stroke="#374151" strokeWidth="2"/>
                  <circle cx="100" cy="100" r="85" fill="#111827"/>
                  <circle cx="100" cy="100" r="20" fill="#374151"/>
                  <g stroke="#9CA3AF" strokeWidth="8" fill="none">
                    <line x1="100" y1="20" x2="100" y2="80"/>
                    <line x1="154" y1="46" x2="115" y2="85"/>
                    <line x1="180" y1="100" x2="120" y2="100"/>
                    <line x1="154" y1="154" x2="115" y2="115"/>
                    <line x1="100" y1="180" x2="100" y2="120"/>
                    <line x1="46" y1="154" x2="85" y2="115"/>
                    <line x1="20" y1="100" x2="80" y2="100"/>
                    <line x1="46" y1="46" x2="85" y2="85"/>
                  </g>
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Brand and Model */}
        <div className="mb-2">
          <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
            {rim.brand} {rim.model}
          </h3>
          <p className="text-sm text-gray-600">{displaySize}</p>
        </div>

        {/* Specs Badges */}
        {specs.length > 0 && (
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {specs.slice(0, 2).map((spec, index) => (
                <span
                  key={index}
                  className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full"
                >
                  {spec}
                </span>
              ))}
              {specs.length > 2 && (
                <span className="text-xs text-gray-500">+{specs.length - 2} more</span>
              )}
            </div>
          </div>
        )}

        {/* Features */}
        {rim.features && rim.features.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {rim.features.slice(0, 2).map((feature, index) => (
                <span
                  key={index}
                  className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full"
                >
                  {feature}
                </span>
              ))}
              {rim.features.length > 2 && (
                <span className="text-xs text-gray-500">+{rim.features.length - 2} more</span>
              )}
            </div>
          </div>
        )}

        {/* Price */}
        <div className="mb-3">
          <span className="text-2xl font-bold text-blue-600">
            R {rim.price.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
          <span className="text-sm text-gray-500 ml-1">per rim</span>
        </div>

      </div>
    </div>
  );
};

export default RimInventoryCard;
